import React from 'react'
import {
  CREATIVE_IMPORT_LIMITS,
  CREATIVE_MEDIA_EDIT_LIMITS,
  FBCallToAction,
} from '@marketing-milk/interfaces'
import { CopyPool, CopyPoolName, DynamicCombination } from './dynamicCombination'
import {
  ActionButton,
  IconButton,
  MediaTile,
  mediaEyebrowStyle as eyebrowStyle,
} from './MediaControls'
import { DraftMedia, MediaEditing, mediaKey, mediaThumb } from './mediaDraft'
import { modalInputStyle } from '../components/PacketModalShell'
import { accentSoft, radius, SurfacePalette, type as typo } from '../tokens'
import { useEditorPalette } from './EditorPalette'

export interface DynamicPoolEditing {
  /** Read-only for a reason the caller states elsewhere (e.g. the ad is published). */
  disabled: boolean
  onChangePool: (pool: CopyPoolName, values: string[]) => void
  /** The buttons a pool entry may be — fewer once a lead form is attached. */
  callsToAction: FBCallToAction[]
  /** Absent for a reviewer who may edit copy but not upload. */
  media?: MediaEditing
}

interface Props {
  assets: DraftMedia[]
  pools: CopyPool[]
  at: DynamicCombination
  onPick: (next: DynamicCombination) => void
  /** Employee copy editing. Absent gives the read-only list a client sees. */
  editing?: DynamicPoolEditing
}

const optionStyle = (palette: SurfacePalette, selected: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  width: '100%',
  padding: '8px 10px',
  textAlign: 'left',
  borderRadius: radius.sm,
  border: `1px solid ${selected ? palette.accent : palette.hairline}`,
  background: selected ? accentSoft : palette.surface,
  cursor: 'pointer',
})

/**
 * The chosen-one marker. A dot rather than a check: a check reads as approval,
 * and at radio-button size it would read as "pick the one to ship" — the opposite
 * of what a dynamic ad does with these.
 *
 * `display: block` is load-bearing, not tidiness: a span is inline by default, so
 * outside a flex parent the width and height are ignored and the dot collapses to
 * a sliver of its own border.
 */
const dotStyle = (palette: SurfacePalette, selected: boolean): React.CSSProperties => ({
  display: 'block',
  flexShrink: 0,
  marginTop: 4,
  width: 12,
  height: 12,
  borderRadius: '50%',
  border: `1px solid ${selected ? palette.accent : palette.textFaint}`,
  background: selected ? palette.accent : 'transparent',
})

/**
 * The dot's hit area, which is most of the control: 12px of visible dot is a
 * quarter of what a pointer can reliably land on, so the button is padded out to
 * a comfortable target and the dot is centred in it. Offset so the dot sits on
 * the field's first line of text rather than the top of its box.
 */
const pickButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  marginTop: 5,
  width: 28,
  height: 28,
  padding: 0,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
}

/**
 * The copy and media a dynamic ad can be assembled from, as lists you read and
 * pick from.
 *
 * Every variant is on screen as text at once, because the reviewer is approving
 * all of them and can only do that having read them. Picking one redraws the ad
 * around it, which is the only way to judge whether a headline works against a
 * given image. Each pool is independent, so four can be held still while the
 * fifth moves.
 *
 * Deliberately not a counter with arrows: that shows one variant, hides the
 * rest, and makes reaching a particular one a matter of cycling past the others.
 *
 * With `editing` the text becomes a field and each pool grows Add/remove, which
 * is the same list — the pick dot stays exactly what it was, a view control that
 * is never persisted.
 */
export function DynamicPoolPicker({ assets, pools, at, onPick, editing }: Props) {
  const palette = useEditorPalette()
  const media = editing?.media
  const mediaBusy = !!media && (media.disabled || media.uploading)
  const selectedAsset = assets[at.asset]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Without this a list of headlines with a selected one reads as "choose
        the headline you want", which is the opposite of what a dynamic ad does —
        and the client, unlike the AE in the importer, has no surrounding context
        to correct the impression. */}
      <p style={{ ...eyebrowStyle(palette), textTransform: 'none', letterSpacing: 0, margin: 0 }}>
        This ad ships with every option below — Facebook mixes one of each when it runs. Pick any
        combination to see how it looks.
      </p>

      {(assets.length > 0 || media) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={eyebrowStyle(palette)}>
              Media ·{' '}
              {media
                ? `${assets.length}/${CREATIVE_MEDIA_EDIT_LIMITS.dynamicMediaMax}`
                : assets.length}
            </span>
            {media && (
              <ActionButton
                label="Add asset"
                disabled={mediaBusy || assets.length >= CREATIVE_MEDIA_EDIT_LIMITS.dynamicMediaMax}
                title={
                  assets.length >= CREATIVE_MEDIA_EDIT_LIMITS.dynamicMediaMax
                    ? `Facebook allows at most ${CREATIVE_MEDIA_EDIT_LIMITS.dynamicMediaMax} assets.`
                    : undefined
                }
                onClick={() => media.onUpload({ kind: 'add-asset' })}
                style={{ marginLeft: 'auto' }}
              />
            )}
          </div>
          <div
            role="group"
            aria-label="Media"
            style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
          >
            {assets.map((asset, index) => (
              <MediaTile
                key={mediaKey(asset)}
                thumb={mediaThumb(asset)}
                size={60}
                selected={index === at.asset}
                label={`Media ${index + 1}`}
                onClick={() => onPick({ ...at, asset: index })}
              />
            ))}
          </div>
          {/* Actions apply to the pinned asset rather than sitting on every tile:
            a 60px thumbnail cannot hold two controls, and the tile the reviewer
            is looking at is the one they mean. */}
          {media && selectedAsset && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <ActionButton
                label="Replace…"
                disabled={mediaBusy}
                onClick={() => media.onUpload({ kind: 'asset', replacing: selectedAsset })}
              />
              <IconButton
                icon="trash"
                label={`Remove media ${at.asset + 1}`}
                disabled={mediaBusy || assets.length <= CREATIVE_MEDIA_EDIT_LIMITS.dynamicMediaMin}
                title={
                  assets.length <= CREATIVE_MEDIA_EDIT_LIMITS.dynamicMediaMin
                    ? 'A dynamic ad needs at least one asset.'
                    : `Remove media ${at.asset + 1}`
                }
                onClick={() => media.onRemoveAsset(selectedAsset)}
              />
            </div>
          )}
        </div>
      )}

      {pools.map(pool => {
        const atCap = pool.values.length >= CREATIVE_IMPORT_LIMITS.dynamicMaxTextVariants
        return (
          <div key={pool.key} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={eyebrowStyle(palette)}>
                {pool.label} ·{' '}
                {editing
                  ? `${pool.values.length}/${CREATIVE_IMPORT_LIMITS.dynamicMaxTextVariants}`
                  : pool.values.length}
              </span>
              {editing && (
                <ActionButton
                  label={`Add ${pool.label.toLowerCase()}`}
                  disabled={editing.disabled || atCap}
                  title={
                    atCap
                      ? `Facebook allows at most ${CREATIVE_IMPORT_LIMITS.dynamicMaxTextVariants} variants per pool.`
                      : undefined
                  }
                  onClick={() =>
                    editing.onChangePool(pool.name, [
                      ...pool.values,
                      pool.cta ? editing.callsToAction[0] : '',
                    ])
                  }
                  style={{ marginLeft: 'auto' }}
                />
              )}
            </div>
            <div
              role="group"
              aria-label={pool.label}
              style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
            >
              {editing && pool.values.length === 0 && (
                <span
                  style={{
                    fontFamily: typo.family.sans,
                    fontSize: typo.size.body,
                    color: palette.textFaint,
                  }}
                >
                  {`No variants — the ad's single ${pool.label.toLowerCase()} is what ships.`}
                </span>
              )}
              {pool.values.map((value, index) => {
                const selected = index === at[pool.key]
                if (!editing) {
                  return (
                    <button
                      key={index}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => onPick({ ...at, [pool.key]: index })}
                      style={optionStyle(palette, selected)}
                    >
                      <span style={dotStyle(palette, selected)} />
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          fontFamily: typo.family.sans,
                          fontSize: typo.size.body,
                          lineHeight: 1.5,
                          color: selected ? palette.text : palette.textMuted,
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {value || '—'}
                      </span>
                    </button>
                  )
                }

                const replace = (next: string) =>
                  editing.onChangePool(
                    pool.name,
                    pool.values.map((existing, i) => (i === index ? next : existing))
                  )

                const pick = () => onPick({ ...at, [pool.key]: index })

                // Selection tints the whole row in the read-only list; here it can
                // only move the border, since a filled input stops reading as one
                // you can type in. Without it a 12px dot carries the entire signal.
                const fieldStyle: React.CSSProperties = {
                  ...modalInputStyle(palette),
                  ...(selected ? { border: `1px solid ${palette.accent}` } : {}),
                }

                return (
                  <div key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    {/* Still a view control, and deliberately never gated on
                      `disabled`: a published ad can't be edited but its variants
                      are exactly what a reviewer wants to page through. */}
                    <button
                      type="button"
                      aria-pressed={selected}
                      aria-label={`Preview ${pool.label.toLowerCase()} ${index + 1}`}
                      onClick={pick}
                      style={pickButtonStyle}
                    >
                      <span style={dotStyle(palette, selected)} />
                    </button>
                    {pool.cta ? (
                      <select
                        aria-label={`${pool.label} ${index + 1}`}
                        value={value}
                        disabled={editing.disabled}
                        onFocus={pick}
                        onChange={e => replace(e.target.value)}
                        style={fieldStyle}
                      >
                        {/* An entry saved before the lead form's rule stays listed, so
                          the select never shows a value other than the one it holds. */}
                        {(editing.callsToAction.includes(value as FBCallToAction)
                          ? editing.callsToAction
                          : [value, ...editing.callsToAction]
                        ).map(option => (
                          <option key={option} value={option}>
                            {option.replace(/_/g, ' ')}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <textarea
                        aria-label={`${pool.label} ${index + 1}`}
                        value={value}
                        rows={pool.multiline ? 3 : 1}
                        disabled={editing.disabled}
                        onFocus={pick}
                        onChange={e => replace(e.target.value)}
                        style={{ ...fieldStyle, resize: 'none' }}
                      />
                    )}
                    <div style={{ marginTop: 5 }}>
                      <IconButton
                        icon="trash"
                        label={`Remove ${pool.label.toLowerCase()} ${index + 1}`}
                        disabled={editing.disabled}
                        onClick={() =>
                          editing.onChangePool(
                            pool.name,
                            pool.values.filter((_, i) => i !== index)
                          )
                        }
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}
